package top.wcpe.allincore.dsl.compiler.codegen

import top.wcpe.allincore.dsl.compiler.ast.ConditionalContentAst
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ForContentAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ModifierAst
import top.wcpe.allincore.dsl.compiler.ast.TooltipContentAst
import top.wcpe.allincore.dsl.compiler.api.IdeMetadata
import top.wcpe.allincore.dsl.compiler.codegen.internal.ExprSerializer
import top.wcpe.allincore.dsl.compiler.semantic.AnimationRefs
import top.wcpe.allincore.dsl.compiler.semantic.EventProps
import top.wcpe.allincore.dsl.compiler.source.SourcePosition
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiModifier
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.dsl.ir.model.UiState
import top.wcpe.allincore.dsl.ir.model.overlay.ContentRef

/**
 * 把 [ElementAst] 递归转换为 [UiNode]。
 *
 * - props 顺序使用 [LinkedHashMap] 严格保留 AST 中的声明顺序；
 * - 名字落在 [EventProps.NAMES] 中的 prop（如 `onClick`、`onChange`）视为事件绑定
 *   （语义阶段已保证为无参事件入口）。
 */
class ElementEmitter(
    private val bindings: BindingEmitter = BindingEmitter(),
    private val canvasEmitter: CanvasEmitter = CanvasEmitter(),
) {

    fun emit(element: ElementAst): UiNode {
        // RFC-RECOMPOSITION：conditional 包装节点走独立 IR 输出路径
        if (element.conditionalContent != null) {
            return emitConditional(element, element.conditionalContent)
        }
        // $for：for 包装节点走独立 IR 输出路径
        if (element.forContent != null) {
            return emitFor(element, element.forContent)
        }

        val props = LinkedHashMap<String, UiBinding>()
        val animationRefs = LinkedHashMap<String, String>()
        val migratedModifiers = mutableListOf<ModifierAst>()
        // 计划 07 M3：animateLayout 是 IR 元素节点级布局动画（FLIP）标记位，不是运行时 prop。
        // 从 props 中识别布尔字面量并迁出，置入 UiNode.animateLayout，避免 runtime 把它当普通 prop。
        var animateLayout = false
        // M6-c Stage 2：数据驱动容器把 source 表达式迁出 props，避免 runtime 把它当普通 prop。
        val hasSource = element.sourceExpr != null
        for (p in element.props) {
            if (hasSource && p.name == "source") {
                continue
            }
            if (p.name == "animateLayout") {
                // 仅布尔字面量 true 才置位；其它（如 false / 非字面量）保持默认 false 且不入 props。
                animateLayout = (p.value as? ExprAst.Literal.BoolLit)?.value == true
                continue
            }
            if (p.name in AnimationRefs.NAMES) {
                val animationId = (p.value as? ExprAst.Literal.StringLit)?.value
                if (animationId != null) {
                    animationRefs[p.name] = animationId
                    continue
                }
            }
            val modifier = modifierFromStyleProp(p)
            if (modifier != null) {
                migratedModifiers += modifier
                continue
            }
            // 事件 prop 在 strict=true 时由 semantic 保证为可解析事件入口；strict=false 时若不是，
            // E301 已在诊断中收集，这里降级按普通绑定处理以避免抛异常打断 codegen。
            val eventName = if (p.name in EventProps.NAMES) eventEntryName(p.value) else null
            val binding = if (eventName != null) {
                bindings.emitEvent(eventName)
            } else {
                bindings.emit(p.value)
            }
            props[p.name] = binding
        }
        if (element.kind == "modelPreview") {
            props[IdeMetadata.modelPreviewBaseProp] = UiBinding.literal(modelPreviewBase(element.position.sourceId))
        }
        val modifiers = emitModifiers(migratedModifiers + element.modifiers)
        // M9.2：声明了 effects { ripple {} } 时，自动绑定 press 触发器播放约定的 "ripple" 动画。
        // 用户在 on { press = "xxx" } 中显式指定时不覆盖。
        if (modifiers.any { it.kind == "ripple" } && "press" !in animationRefs) {
            animationRefs["press"] = "ripple"
        }
        val children = element.children.map { emit(it) }
        // M11.6：canvas 元素携带 canvasData（DrawBlob 字节码 + 循环参数），其它 kind 保持 null。
        val canvasData = element.canvasData?.let { canvasEmitter.emit(it) }
        val contentRef = emitToastContent(element)
        // M6-c Stage 2：hasSource 时把 sourceExpr 序列化为 blob，写入 UiNode source 段；
        // 普通容器 sourceExpr=null、sourceParams=emptyList()。
        val sourceExpr = element.sourceExpr
        if (sourceExpr == null) {
            return UiNode(
                element.kind,
                element.id,
                props,
                animationRefs,
                children,
                canvasData,
                null,
                emptyList(),
                contentRef,
                null,
                null,
                null,
                null,
                false,
                null,
                animateLayout,
                modifiers,
            )
        }
        val sourceBlob = ExprSerializer.serializeExpr(sourceExpr)
        val sourceParamNames = element.sourceParams.map { it.name }
        return UiNode(
            element.kind,
            element.id,
            props,
            animationRefs,
            children,
            canvasData,
            sourceBlob,
            sourceParamNames,
            contentRef,
            null,
            null,
            null,
            null,
            false,
            null,
            animateLayout,
            modifiers,
        )
    }

    private fun emitModifiers(modifiers: List<ModifierAst>): List<UiModifier> {
        if (modifiers.isEmpty()) {
            return emptyList()
        }
        return modifiers.map { modifier ->
            val fields = LinkedHashMap<String, UiBinding>()
            modifier.condition?.let { fields["__condition"] = bindings.emit(it) }
            for (field in modifier.fields) {
                fields[field.name] = bindings.emit(field.value)
            }
            UiModifier(modifier.kind, fields)
        }
    }

    /**
     * 兼容迁移入口：样式类 pass 仍会把样式规则合并到 props，这里在入 IR 前提升为 v32 modifier。
     */
    private fun modifierFromStyleProp(prop: ElementAst.PropAssign): ModifierAst? {
        val effectPrefix = "effect."
        if (prop.name.startsWith(effectPrefix)) {
            val rest = prop.name.substring(effectPrefix.length)
            val dot = rest.indexOf('.')
            if (dot <= 0 || dot == rest.length - 1) {
                return null
            }
            val field = ElementAst.PropAssign(rest.substring(dot + 1), prop.value, prop.position)
            return ModifierAst(rest.substring(0, dot), listOf(field), prop.position)
        }
        if (!isStyleModifierProp(prop.name)) {
            return null
        }
        return ModifierAst("style", listOf(prop), prop.position)
    }

    private fun isStyleModifierProp(name: String): Boolean =
        name == "background" ||
            name == "borderWidth" ||
            name == "borderColor" ||
            name == "cornerRadius" ||
            name == "cornerRadiusTL" ||
            name == "cornerRadiusTR" ||
            name == "cornerRadiusBR" ||
            name == "cornerRadiusBL" ||
            name == "gradientType" ||
            name == "gradientAngle" ||
            name == "gradientStops" ||
            name == "gradientStartColor" ||
            name == "gradientEndColor" ||
            name == "radialCenterX" ||
            name == "radialCenterY" ||
            name == "radialRadius" ||
            name == "shadowOffsetX" ||
            name == "shadowOffsetY" ||
            name == "shadowBlur" ||
            name == "shadowColor" ||
            name == "shadowSpread" ||
            name == "shadowInset" ||
            name == "boxShadows" ||
            name == "mask" ||
            name == "maskParam1" ||
            name == "maskParam2" ||
            name == "maskParam3" ||
            name == "mask.pie.startAngle" ||
            name == "mask.pie.sweepAngle"

    private fun emitToastContent(element: ElementAst): ContentRef? = when (val content = element.toastContent) {
        null -> null
        is TooltipContentAst.Text -> null
        is TooltipContentAst.Inline -> ContentRef.Inline(inlineIr(element.id, content.root))
        is TooltipContentAst.Component -> ContentRef.Component(
            content.name,
            content.props.associate { it.name to literalPropValue(it.value) },
        )
    }

    /**
     * RFC-RECOMPOSITION：把 ConditionalContentAst 编译为带 selectorExpr 的 conditional UiNode。
     *
     * 输出结构：
     *   UiNode {
     *     kind = $cond
     *     id = "$cond_<line>_<col>"
     *     children = [branch_0_root, branch_1_root, ..., else_root?]
     *     selectorExpr = 嵌套 Ternary 表达式，求值结果是命中 children 索引或 -1（无命中且无 else）
     *   }
     *
     * 嵌套 Ternary 的拼装：
     *   - if 形态：`branch_0.cond ? 0 : (branch_1.cond ? 1 : ... : (hasElse ? N : -1))`
     *   - when 形态：每个 branch.condition 由 `subject == values[0] || subject == values[1] || ...` 拼成
     */
    private fun emitConditional(element: ElementAst, content: ConditionalContentAst): UiNode {
        // RFC-RECOMPOSITION 不变量：包装节点由 wrapConditional 产出，永远不带 props /
        // animationRefs / canvasData / sourceExpr / tooltip / toast / menu 等元素级声明，
        // 实际分支元素由 conditionalContent 携带。这里 fail-fast 防止后续维护者意外
        // 在包装节点上挂载属性而被静默丢弃。
        require(element.props.isEmpty()) {
            "conditional 包装节点不应携带 props，实际：${element.props.map { it.name }}（pos=${element.position}）"
        }
        require(element.children.isEmpty()) {
            "conditional 包装节点 children 必须为空，实际 size=${element.children.size}（pos=${element.position}）"
        }
        require(element.canvasData == null) {
            "conditional 包装节点不应携带 canvasData（pos=${element.position}）"
        }
        require(element.sourceExpr == null && element.sourceParams.isEmpty()) {
            "conditional 包装节点不应同时是数据驱动容器（pos=${element.position}）"
        }
        require(element.tooltipContent == null && element.toastContent == null) {
            "conditional 包装节点不应携带 tooltip / toast 内容（pos=${element.position}）"
        }
        require(element.menuBlocks.isEmpty()
            && element.rightClickMenuContent == null
            && element.contextMenuContent == null) {
            "conditional 包装节点不应携带菜单内容（pos=${element.position}）"
        }

        // 收集分支 IR：先 N 个 branch.body，最后 elseBranch（如果存在）
        val branchNodes = ArrayList<UiNode>(content.branches.size + 1)
        for (branch in content.branches) {
            branchNodes += emit(branch.body)
        }
        content.elseBranch?.let { branchNodes += emit(it) }

        // 构造 selectorExpr：从尾巴往前嵌套 Ternary
        val selectorExpr = buildSelectorExpression(content)
        val selectorBlob = ExprSerializer.serializeExpr(selectorExpr)

        return UiNode.conditional(element.id, branchNodes, selectorBlob)
    }

    /**
     * $for：把 ForContentAst 编译为带 iterableExpr 的 for UiNode。
     *
     * 输出结构：
     *   UiNode {
     *     kind = $for
     *     id = "$for_<line>_<col>"
     *     children = [body_root]          // 单根循环体模板
     *     iterableExpr = 序列化后的可迭代表达式 blob
     *     forVar1 / forVar2 / forOverMap  // 循环变量与形态
     *   }
     */
    private fun emitFor(element: ElementAst, content: ForContentAst): UiNode {
        // 与 emitConditional 同理：包装节点不应携带任何元素级声明，fail-fast 防止静默丢弃。
        require(element.props.isEmpty()) {
            "for 包装节点不应携带 props，实际：${element.props.map { it.name }}（pos=${element.position}）"
        }
        require(element.children.isEmpty()) {
            "for 包装节点 children 必须为空，实际 size=${element.children.size}（pos=${element.position}）"
        }
        require(element.canvasData == null) {
            "for 包装节点不应携带 canvasData（pos=${element.position}）"
        }
        require(element.sourceExpr == null && element.sourceParams.isEmpty()) {
            "for 包装节点不应同时是数据驱动容器（pos=${element.position}）"
        }
        require(element.tooltipContent == null && element.toastContent == null) {
            "for 包装节点不应携带 tooltip / toast 内容（pos=${element.position}）"
        }
        require(element.menuBlocks.isEmpty()
            && element.rightClickMenuContent == null
            && element.contextMenuContent == null) {
            "for 包装节点不应携带菜单内容（pos=${element.position}）"
        }

        // 单根循环体 → 单模板（运行时按可迭代源逐项实例化）
        val bodyNode = emit(content.body)
        val iterableBlob = ExprSerializer.serializeExpr(content.iterable)
        val keyBlob = content.keyExpr?.let { ExprSerializer.serializeExpr(it) }
        return UiNode.forEach(
            element.id,
            listOf(bodyNode),
            iterableBlob,
            content.var1,
            content.var2,
            content.overMap,
            keyBlob,
        )
    }

    /**
     * 构造 selector 表达式：返回一个 ExprAst，求值为命中 children 索引（或 -1 未命中）。
     *
     * 算法：
     *   acc = (hasElse ? branchCount : -1)  作为最末"未命中"分支
     *   for i in (branches.size - 1) downTo 0：
     *     branchCondExpr = if 形态 ? branch.condition : (subject == values[0] || subject == values[1] || ...)
     *     acc = Ternary(branchCondExpr, NumberLit(i), acc)
     */
    private fun buildSelectorExpression(content: ConditionalContentAst): ExprAst {
        val pos = content.position
        val hasElse = content.elseBranch != null
        // 最末 fallback：有 else 时索引指向 children[branches.size]，无 else 时 -1
        val fallbackIndex: Double = if (hasElse) content.branches.size.toDouble() else -1.0
        var acc: ExprAst = ExprAst.Literal.NumberLit(fallbackIndex, pos)

        for (i in content.branches.indices.reversed()) {
            val branch = content.branches[i]
            val branchCond: ExprAst = if (branch.condition != null) {
                // if 形态 / 无 subject when 形态：直接使用条件表达式
                branch.condition
            } else {
                // when (subject) 值匹配形态：subject == values[0] || subject == values[1] || ...
                buildWhenBranchCondition(content.subject!!, branch.values, branch.position)
            }
            acc = ExprAst.Ternary(
                cond = branchCond,
                thenExpr = ExprAst.Literal.NumberLit(i.toDouble(), branch.position),
                elseExpr = acc,
                position = branch.position,
            )
        }
        return acc
    }

    /** 构造 when 分支的合并条件：subject == v1 || subject == v2 || ... */
    private fun buildWhenBranchCondition(
        subject: ExprAst,
        values: List<ExprAst>,
        pos: SourcePosition,
    ): ExprAst {
        require(values.isNotEmpty()) { "when 分支必须有至少一个 value（pos=$pos）" }
        var acc: ExprAst = ExprAst.BinaryOp("==", subject, values[0], pos)
        for (i in 1 until values.size) {
            acc = ExprAst.BinaryOp(
                "||",
                acc,
                ExprAst.BinaryOp("==", subject, values[i], pos),
                pos,
            )
        }
        return acc
    }

    private fun inlineIr(hostId: String, root: ElementAst): UiIr =
        UiIr("$hostId#toast", UiState.empty(), emptyMap(), emit(root))

    private fun literalPropValue(expr: ExprAst): Any? = when (expr) {
        is ExprAst.Literal.StringLit -> expr.value
        is ExprAst.Literal.NumberLit -> expr.value.toDouble()
        is ExprAst.Literal.BoolLit -> expr.value
        else -> null
    }

    /** modelPreview 的资源基准目录取声明节点 sourceId 的规范父目录。 */
    private fun modelPreviewBase(sourceId: String): String =
        sourceId.substringBeforeLast('/', missingDelimiterValue = "")

    private fun eventEntryName(expr: ExprAst): String? = when (expr) {
        // M6-e：纯 `::name` 才视为事件入口；`::name(args)` 是部分应用，按表达式 binding 走
        is ExprAst.FunRefExpr -> if (expr.boundArgs.isEmpty() && expr.boundNamedArgs.isEmpty()) expr.name else null
        is ExprAst.CallExpr -> {
            val callee = expr.callee
            if (expr.args.isNotEmpty()) null
            else when (callee) {
                is ExprAst.FunRefExpr -> if (callee.boundArgs.isEmpty() && callee.boundNamedArgs.isEmpty()) callee.name else null
                is ExprAst.IdentRef -> callee.name
                else -> null
            }
        }
        else -> null
    }
}
